import React from 'react';

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Sista skyddsnätet: ett oväntat renderingsfel ska ge en begriplig sida med
 * en väg vidare, inte en helt vit skärm mitt i ett köp.
 *
 * Laddningsfel för lazy-chunks (vanligt direkt efter en ny deploy när
 * webbläsaren har en gammal index.html) löses oftast av en omladdning.
 */
export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: unknown, info: React.ErrorInfo) {
        console.error('[ErrorBoundary]', error, info.componentStack);
    }

    private handleReload = () => {
        window.location.reload();
    };

    private handleHome = () => {
        window.location.hash = '#/';
        window.location.reload();
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <div role="alert" className="bg-white max-w-md w-full rounded-3xl border border-stone-100 shadow-xl p-8 text-center">
                    <h1 className="text-2xl font-heading font-bold text-coffee-900 mb-3">Något gick fel</h1>
                    <p className="text-sm text-stone-600 leading-relaxed mb-8">
                        Sidan kunde inte visas. Ladda om och försök igen — din varukorg finns kvar.
                        Om felet kvarstår, kontakta oss så hjälper vi dig.
                    </p>
                    <div className="flex flex-col gap-3">
                        <button
                            type="button"
                            onClick={this.handleReload}
                            className="w-full py-3.5 bg-coffee-900 text-white text-xs font-black rounded-xl uppercase tracking-widest hover:bg-coffee-800 transition-colors"
                        >
                            Ladda om sidan
                        </button>
                        <button
                            type="button"
                            onClick={this.handleHome}
                            className="w-full py-3 text-xs font-bold text-stone-500 hover:text-stone-900 transition-colors"
                        >
                            Till startsidan
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
